import { v1 as uuidv1 } from 'uuid'
import { webRequestClient } from '@/services'
import { errorHandler } from '@/utils/helpers'
import helpers from './helpers'
import commonActions from '@/store/data/common/actions'

import { get, isNil, toNumber, isEmpty, find, head, isEqual, size, upperFirst } from 'lodash'

const actions = {
    retrieveItems({ commit, state, getters, rootGetters }) {
        commit('setIsLoading', true)

        const _params = {
            params: {
                list_limit: 0, // # page size
                // list_offset: 10, # offset element numbers
                // order_fields: 'end_date', # sort target field
                // order_direction: 'asc', # sort order
                conditions: {
                    //
                    // label: {
                    //     '-like': `%${Dacia}%`
                    // },
                    // id: {
                    //     '-like': '%133%'
                    // },
                    account_id: state.accountId,
                    status: ['active', 'inactive'],
                    with_tags: 1,
                }
            }
        }

        return webRequestClient
            .get('/audience_admin/audiences.json', _params)
            .then(audiences => {
                if (get(audiences, 'status', '') === 'KO') {
                    errorHandler('Error while retrieving audiences', commit)
                } else if (!isEmpty(audiences.list)) {
                    commit('setRetrievedItems', {
                        items: helpers.parseData(audiences.list)
                    })
                    commit('setCollection', helpers.instanciateAll(audiences.list))
                    if (
                        rootGetters['data/settings/getSocioDemoValue']({
                            settingName: 'geography'
                        })
                    ) {
                        commit(
                            'data/georadar/setRetrievedItems',
                            {
                                items: getters.getAudiencesGeoradar
                            },
                            { root: true }
                        )
                    }
                }
            })
            .catch(error => {
                commit('setAudiencesActiveError', true)
                errorHandler(error, commit)
            })
            .finally(() => {
                commit('setIsLoading', false)
            })
    },

    retrieveAudience({ commit }, params) {
        const { fromTransfer } = params
        const _params = {
            params: {}
        }

        return webRequestClient
            .get(`/audience_admin/audiences/${params.audienceId}.json`, _params)
            .then(audience => {
                if (get(audience, 'status', '') === 'KO') {
                    errorHandler('Error while retrieving audience', commit)
                } else {
                    // add fromTransfer params to not break other conditions
                    if (fromTransfer || !isEqual(audience.initial_end_date, audience.end_date)) {
                        commit('setEditedAudienceSpecificField', { audienceId: params.audienceId, field: 'endDate', value: audience.end_date })
                    }
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    retrieveAudienceVolumeFromAudience({ commit, rootState }, { audienceId }) {
        const accountId = toNumber(rootState.account.accountId)

        commit('ui/monitoring/setIsLoading', { item: 'volumesChart', isLoading: true }, { root: true })

        return webRequestClient
            .get(`/audience_admin/audiences/${audienceId}/audience_volumes.json`, { params: { account_id: accountId } })
            .then(audienceVolumeDetail => {
                if (!isEmpty(audienceVolumeDetail)) {
                    commit('ui/monitoring/setIsLoading', { item: 'volumesChart', isLoading: false }, { root: true })
                    const keys = Object.keys(audienceVolumeDetail)
                    const chartData = keys
                        .sort()
                        .map(function(key) {
                            return [key, toNumber(audienceVolumeDetail[key])]
                        })
                    commit('setAudienceDetails', {
                        items: chartData,
                        audienceId
                    })
                } else {
                    commit('ui/monitoring/setIsLoading', { item: 'volumesChart', isLoading: false }, { root: true })
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'warning',
                            message: 'There is no detail for this audience'
                        },
                        { root: true }
                    )
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    retrieveVolumes({ dispatch }, params) {
        const audiences = params.paginatedData
        for (const audience of audiences) {
            //API rejects call if audience is not active
            if (isNil(audience.volumes)) {
                dispatch('processAudienceStatistics', {
                    formula: {
                        operator: 'intersection',
                        children: [{ operator: 'union', children: [{ type: 'Audience', id: [audience.id], recency: parseInt(audience.maxRecency) }] }]
                    },
                    audienceId: audience.id,
                    type: 'volumes'
                })
            }
        }
    },

    /**
     * Launch the request to retrieve information about an audience
     * if this information is not already in the store
     *
     * @param {Object} context - vuex context
     * @param {Object} payload
     * @param {String} payload.audienceId
     * @param {Object} payload.formula - The formula to send in params to WAM api
     * @param {String} [payload.type=summary] - Type of information to be retrieved
     */
    retrieveAudienceInfos({ dispatch, state }, { audienceId, formula, type = 'summary' }) {
        const audience = find(state.list, item => item.id === audienceId)
        if (type.toLowerCase() === 'breakdown' || (audience && isEmpty(audience[type]))) {
            dispatch('processAudienceStatistics', { formula, audienceId, type })
        }
    },

    /**
     * Request process to retrieve and store information about an audience
     * Include a retry system if the server is not ready
     *
     * @param {Object} context - vuex context
     * @param {Object} payload
     * @param {String} payload.audienceId
     * @param {Object} payload.formula - The formula to send in params to WAM api
     * @param {String} payload.type - Type of information to be retrieved
     * @param {String} payload.requestKey - Unique uuid to identify request and handle multiple call
     * @param {Number} [payload.requestTry=0] - Number of times the request has already been executed
     *
     * @returns {Promise}
     */
    processAudienceStatistics({ commit, dispatch, rootState }, { audienceId, formula, type, requestId, requestTry = 0 }) {
        const loadingType = type === 'volumes' ? `volume/${audienceId}` : type
        // To prevent having requests of the same type on different audiences simultaneously.
        if (!requestId || requestId === rootState.ui.monitoring.lastUuRequestKey[loadingType]) {
            requestId = requestId || uuidv1()
            const requestParams = {
                formula,
                account_id: get(rootState, 'account.accountId', ''),
                method: type === 'volumes' ? 'count' : type,
                version: type === 'volumes' ? 2 : 1
            }

            commit('ui/monitoring/setIsLoading', { item: loadingType, isLoading: true }, { root: true })
            commit('ui/monitoring/setLastUuRequestKey', { item: loadingType, key: requestId }, { root: true })

            const handleError = () => {
                commit('ui/monitoring/setIsLoading', { item: loadingType, isLoading: false }, { root: true })
                errorHandler(`Unable to retrieve ${type} from audience ${audienceId}`, commit)
            }

            webRequestClient
                .post('/audience_admin/criteria/statistic.json', requestParams)
                .then(res => {
                    // No response or functionnal error
                    if (isEmpty(res) || res.status === 'KO') {
                        handleError()
                    }

                    // Response not ready - retry max 30 times
                    else if (res.status === 'NOT_READY') {
                        if (requestTry < 30) {
                            setTimeout(() => dispatch('processAudienceStatistics', { formula, type, audienceId, requestId, requestTry: requestTry + 1 }), 2000)
                        } else {
                            handleError()
                        }
                    }

                    // Everything seems ok
                    else {
                        if (type === 'volumes') {
                            commit('setAudienceVolumes', { audienceId, volumes: res.formula.volume, volumeType: 'total_desktop' })
                        } else {
                            commit(`setAudience${upperFirst(type)}`, { audienceId, [type]: res[type] || res })
                        }

                        commit('ui/monitoring/setIsLoading', { item: loadingType, isLoading: false }, { root: true })
                    }
                })
                .catch(err => {
                    handleError(err)
                })
        }
    },

    createAudience({ commit }, params) {
        const payload = params
        const path = params.above_limit || params.below_limit ? '/audience_admin/audiences/audiences_volume_limits/.json' : '/audience_admin/audiences.json'
        if (typeof params.email_cc === 'string' && size(params.email_cc) > 1) {
            payload.email_cc = params.email_cc // opt
        }

        // return Promise otherwise tags won't be created
        return webRequestClient
            .post(path, payload)
            .then(response => {
                if (response.status === 'active') {
                    commit('setCreatedItem', {
                        item: head(helpers.parseData([response]))
                    })
                    commit('setAudienceInCollection', helpers.instanciate(response))
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Audience successfully created'
                        },
                        { root: true }
                    )
                    commit('ui/volumeAlert/setDefaultVolumeAlertForm', {}, { root: true })
                    return response
                } else {
                    errorHandler('Audience could not be created', commit)
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    editAudience({ commit }, { id, notificationMessage, params, tags }) {
        const path = `/audience_admin/audiences/${id}.json`

        return webRequestClient
            .put(path, params)
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler(`error while updating audience ${id}`, commit)
                } else if (!isEmpty(response)) {
                    const updatedAudience = helpers.apiToStore({ ...response, tags: tags })

                    commit('setEditedAudience', { audience: updatedAudience })
                    commit('setAudienceInCollection', helpers.instanciate({ ...response, tags }))
                    commit('setChangeRowData', updatedAudience)
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: notificationMessage
                        },
                        { root: true }
                    )
                }
            })
            .catch(error => {
                errorHandler(error, commit)
                commit('setChangeRowData', null)
            })
    },

    addNewAudienceTags({ commit }, { id, account_id, tags }) {
        return webRequestClient
            .post('/audience_admin/audiences/tags/json', { id, account_id, tags })
            .then(response => {
                const newTags = response.tags
                if (!isEmpty(response) && get(response, 'status', '') !== 'KO') {
                    commit('addAudienceTagInCollection', {
                        audienceId: id,
                        newTags
                    })
                    return response
                } else {
                    const _errorMessage = isEmpty(get(response, 'message', '')) ? 'Tags creation failed' : response.message
                    errorHandler(_errorMessage, commit)
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    deleteTagFromAudience({ commit, rootState }, { audience, tagId, update = true }) {
        const audienceTagsListUpdated = audience.tags.filter(item => item.id !== tagId)
        const tag_ids = JSON.stringify([tagId])
        const path = '/audience_admin/audiences/tags/json'

        return webRequestClient
            .delete(path, { params: { account_id: rootState.account.accountId, id: audience.id, tag_ids } })
            .then(response => {
                if (!isEmpty(response)) {
                    if (update) {
                        commit('setEditedAudienceSpecificField', {
                            audienceId: audience.id,
                            field: 'tags',
                            value: audienceTagsListUpdated
                        })
                        commit('removeAudienceTagInColltection', {
                            audienceId: audience.id,
                            tagId
                        })
                    }
                } else {
                    errorHandler('Error while deleting', commit)
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    deleteAudience({ commit, rootState }, params) {
        return webRequestClient
            .delete(`/audience_admin/audiences/${params.audienceId}.json`)
            .then(deletedAudience => {
                if (get(deletedAudience, 'status', '') === 'KO' || isEmpty(deletedAudience)) {
                    errorHandler(`error while deleting audience ${params.audienceId}`, commit)
                } else {
                    if (toNumber(rootState.ui.monitoring.selectedRowId) === toNumber(params.audienceId)) {
                        commit('ui/monitoring/setSelectedRow', { rowId: null }, { root: true })
                    }
                    if(!isEmpty(rootState.data.transfer.transferInfos) && +rootState.data.transfer.transferInfos.audience_id === +params.audienceId ) {
                        commit('ui/transfers/setSelectedTransferRowId', null, { root: true })
                        commit('data/transfer/setTransferInfos', null, { root: true })
                    }
                    if (rootState.builder?.audienceInEditionId === `Audience_${params.audienceId}`) {
                        commit('builder/setAudienceInEditionId', undefined, { root: true })
                        commit('builder/resetFormula', true, { root: true })
                    }
                    commit('removeAudience', { audienceId: deletedAudience.id })
                    commit('removeAudienceInCollection', `Audience_${deletedAudience.id}`)
                    commit('data/transfer/removeTransfersFromAudienceId', { audienceId: deletedAudience.id }, { root: true })

                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Your audience has been successfully deleted'
                        },
                        { root: true }
                    )
                }
                commit('ui/monitoring/setDisplayModal', { item: 'audience', display: false }, { root: true })
                commit('ui/monitoring/setDeletedAudienceId', { audienceId: null }, { root: true })
                commit('setResetBulkSelectedRows', true)
            })
            .catch(error => {
                commit('ui/monitoring/setDisplayModal', { item: 'audience', display: false }, { root: true })
                commit('ui/monitoring/setDeletedAudienceId', { audienceId: null }, { root: true })
                errorHandler(error, commit)
            })
    },

    /**
     * Request process to get ans store from backend volume alert for an audience
     *
     * @param {Object} context - vuex context
     * @param {Object} payload
     * @param {String|Number} payload.audienceId
     *
     * @returns {Promise}
     */
    retrieveVolumeAlert({ commit, rootState }, { audienceId }) {
        const path = '/audience_admin/audiences_volume_limits.json'
        const params = {
            account_id: toNumber(rootState.account.accountId),
            conditions: { audience_id: audienceId }
        }

        commit('setAudienceVolumeAlert', { audienceId, volumeAlert: { isLoading: true } })

        return webRequestClient
            .get(path, { params })
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('error while retrieving volume alert', commit)
                } else if (!isEmpty(response.list)) {
                    const details = {
                        ...head(response.list),
                        email_audience_limit: head(response.list).email,
                        email_cc_audience_limit: isNil(head(response.list).email_cc) ? '[]' : head(response.list).email_cc
                    }
                    delete details.email
                    delete details.email_cc
                    commit('setAudienceVolumeAlert', { audienceId, volumeAlert: { details } })
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
            .finally(() => {
                commit('setAudienceVolumeAlert', { audienceId, volumeAlert: { isLoading: false } })
            })
    },

    /**
     * Request process to delete volume alert for an audience
     *
     * @param {Object} context - vuex context
     * @param {Object} payload
     * @param {String|Number} payload.audienceId
     * @param {String|Number} payload.volumeAlertId
     *
     * @returns {Promise}
     */
    deleteVolumeAlert({ commit, rootState }, { audienceId, volumeAlertId }) {
        const path = `/audience_admin/audiences_volume_limits/${volumeAlertId}.json`
        const params = { account_id: toNumber(rootState.account.accountId) }

        return webRequestClient
            .delete(path, { params })
            .then(response => {
                if (!isEmpty(response)) {
                    commit('setAudienceVolumeAlert', { audienceId, volumeAlert: { details: {} } })
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Volume alert has been successfully deleted'
                        },
                        { root: true }
                    )
                    commit('ui/monitoring/setDisplayModal', { item: 'volumeAlert', display: false }, { root: true })
                    commit('ui/monitoring/setDeletedVolumeAlertId', { volumeAlertId: null }, { root: true })
                } else {
                    errorHandler('Error while deleting volume alert', commit)
                }
            })
            .catch(error => {
                commit('ui/monitoring/setDisplayModal', { item: 'volumeAlert', display: false }, { root: true })
                commit('ui/monitoring/setDeletedVolumeAlertId', { volumeAlertId: null }, { root: true })
                errorHandler(error, commit)
            })
    },

    /**
     * Request to edit volume alert for an audience
     *
     * @param {Object} context - vuex context
     * @param {Object} payload
     * @param {String|Number} payload.audienceId
     * @param {Object} payload.volumeAlertParams
     *
     * @returns {Promise}
     */
    editVolumeAlert({ commit, dispatch, rootState }, { audienceId, volumeAlertParams }) {
        const path = `/audience_admin/audiences_volume_limits/${volumeAlertParams.id}.json`
        const params = {
            ...volumeAlertParams,
            account_id: toNumber(rootState.account.accountId),
            email: volumeAlertParams.email_audience_limit,
            email_cc: volumeAlertParams.email_cc_audience_limit
        }
        delete params.email_audience_limit
        delete params.email_cc_audience_limit

        return webRequestClient
            .put(path, params)
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('Error while editing volume alert', commit)
                } else {
                    dispatch('retrieveVolumeAlert', { audienceId })
                    // clear Volume Alert
                    commit('ui/volumeAlert/setDefaultVolumeAlertForm', {}, { root: true })
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Volume alert has been successfully edited'
                        },
                        { root: true }
                    )
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    /**
     * Request to create volume alert for an audience
     *
     * @param {Object} context - vuex context
     * @param {Object} payload
     * @param {Object} volumeAlertParams
     *
     * @returns {Promise}
     */
    createVolumeAlert({ commit, dispatch, rootState }, volumeAlertParams) {
        const path = '/audience_admin/audiences_volume_limits.json'
        const params = {
            ...volumeAlertParams,
            account_id: toNumber(rootState.account.accountId),
            email: volumeAlertParams.email_audience_limit,
            email_cc: volumeAlertParams.email_cc_audience_limit
        }
        delete params.email_audience_limit
        delete params.email_cc_audience_limit

        return webRequestClient
            .post(path, params)
            .then(response => {
                if (isEmpty(response) || response.status === 'KO') {
                    errorHandler('Error while creating volume alert', commit)
                } else if (!isEmpty(response)) {
                    dispatch('retrieveVolumeAlert', { audienceId: volumeAlertParams.audience_id })
                    // clear Volume Alert
                    commit('ui/volumeAlert/setDefaultVolumeAlertForm', {}, { root: true })
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Volume alert has been successfully created'
                        },
                        { root: true }
                    )
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    bulkEditAudiences({ commit, _dispatch, rootState }, params) {
        commit('setIsUpdatedOrDeletedLoading', true)
        const path = '/audience_admin/audiences/bulk_update/json'
        const requestParams = {
            account_id: toNumber(rootState.account.accountId),
            ids: params.audiences,
            fields: params.fields
        }

        return webRequestClient
            .post(path, requestParams)
            .then(response => {
                if (isEmpty(response) || response.status === 'KO') {
                    errorHandler('Error while editing bulk audiences', commit)
                } else if (!isEmpty(response)) {
                    response.forEach(audience => {
                        const updatedAudience = helpers.apiToStore({ ...audience, tags: params.tags })
                        commit('setEditedAudience', { audience: updatedAudience })
                        commit('setAudienceInCollection', helpers.instanciate(audience))
                    })
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Audiences successfully updated'
                        },
                        { root: true }
                    )
                }
                commit('setIsUpdatedOrDeletedLoading', false)
            })
            .catch(error => {
                commit('setIsUpdatedOrDeletedLoading', false)
                errorHandler(error, commit)
            })
    },

    /**
     * Recieves a list of bulk audiences and returns the total volumes of their formulas
     * @param {*} bulkAudiences  list of audiences to bulk bootstrap
     * @returns {Promise} total volumes of all selected audiences
     */
    async bulkRetrieveAudiencesVolumes({ commit, _dispatch, rootState }, bulkAudiences) {
        let totalVolumes = 0
        const promises = []
        bulkAudiences.forEach(audience => {
            const requestParams = {
                formula: audience.formula,
                account_id: get(rootState, 'account.accountId', ''),
                method: 'count',
                version: 1
            }
            promises.push(
                webRequestClient
                    .post('/audience_admin/criteria/statistic.json', requestParams)
                    .then(res => {
                        // No response or functionnal error
                        if (isEmpty(res) || res.status === 'KO') {
                            errorHandler(`Unable to retrieve volumes from audience ${audience.id}`, commit)
                        } else {
                            totalVolumes += res.total_doc
                        }
                    })
                    .catch(() => {
                        errorHandler(`Unable to retrieve volumes from audience ${audience.id}`, commit)
                    })
            )
        })
        return Promise.all(promises).then(() => {
            return totalVolumes
        })
    },

    /**
     * Recieves a list of bulk audiences to delete
     * @param {*} bulkAudiences  list of audiences to bulk delete
     * @returns {Promise} deleted audiences
     */
    bulkDeleteAudiences({ commit, _dispatch, rootState }, audiences) {
        commit('setIsUpdatedOrDeletedLoading', true)
        const params = {
            account_id: rootState.account.accountId,
            ids: audiences
        }
        return webRequestClient
            .post('/audience_admin/audiences/bulk_delete/json', params)
            .then(deletedAudiences => {
                if (get(deletedAudiences, 'status', '') === 'KO') {
                    errorHandler('error while deleting audiences', commit)
                } else {
                    if (audiences.includes(toNumber(rootState.ui.monitoring.selectedRowId))) {
                        commit('ui/monitoring/setSelectedRow', { rowId: null }, { root: true })
                    }
                    if(!isEmpty(rootState.data.transfer.transferInfos) && audiences.includes(+rootState.data.transfer.transferInfos.audience_id)) {
                        commit('ui/transfers/setSelectedTransferRowId', null, { root: true })
                        commit('data/transfer/setTransferInfos', null, { root: true })
                    }
                    audiences.forEach(aud => {
                        if (rootState.builder?.audienceInEditionId === `Audience_${aud}`) {
                            commit('builder/setAudienceInEditionId', undefined, { root: true })
                            commit('builder/resetFormula', true, { root: true })
                        }
                        commit('removeAudience', { audienceId: aud })
                        commit('removeAudienceInCollection', `Audience_${aud}`)
                        commit('data/transfer/removeTransfersFromAudienceId', { audienceId: aud }, { root: true })
                    })

                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Your audiences have been deleted'
                        },
                        { root: true }
                    )
                }
                commit('setIsUpdatedOrDeletedLoading', false)
            })
            .catch(error => {
                commit('setIsUpdatedOrDeletedLoading', false)
                errorHandler(error, commit)
            })
    },

    resetEditBulkForm({ commit }, reset) {
        commit('setResetBulkSelectedRows', reset)
    }
}

export default { ...commonActions, ...actions }
